import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { RedirectKeepUTM } from "../../../RedirectKeepUTM";

import { formattedDate } from "../../../../formattedDate";
import {
  DownloadIcon,
  FileIcon,
  FolderIcon,
  LockIcon as LockIconShared,
  TrashIcon,
} from "../../../../icons";
import { ConfirmDialog } from "../../../confirmDialog";
import { SortableTableHead } from "../../../sortableTableHead";
import { HeaderBrowser } from "../headerBrowser";
import { useStyles } from "./content-files.styles";

const styleHeader = {
  textAlign: "left",
  fontFamily: "Lato",
  fontWeight: "600",
  fontSize: "16px",
  lineHeight: "19px",
};

const headers = [
  {
    id: "name",
    label: "Name",
    sortable: false,
    numeric: false,
    disablePadding: false,
    cellProps: {
      style: styleHeader,
    },
  },
  {
    id: "size",
    label: "Size",
    sortable: true,
    numeric: true,
    disablePadding: false,
    cellProps: {
      style: styleHeader,
    },
  },
  {
    id: "dateUtc",
    label: "Date",
    sortable: true,
    numeric: false,
    disablePadding: false,
    cellProps: {
      style: styleHeader,
    },
  },
  {
    id: "createdBy",
    label: "Created By",
    sortable: true,
    numeric: false,
    disablePadding: false,
    cellProps: {
      style: styleHeader,
    },
  },

  {
    id: "actions",
    label: "",
    sortable: false,
    numeric: false,
    disablePadding: false,
    cellProps: {
      style: { ...styleHeader, textAlign: "center" },
    },
  },
];
const INITIALTYPEDELETE = {
  message: "",
  type: 2,
  isOpen: false,
  bodyMessage: "",
};

interface FileToRemove {
  id?: string;
  name?: string;
}

export const ContentFiles = ({
  handleDownloadFile,
  isSearching,
  deep,
  files,
  folderSelected,
  handleDeleteFolder,
  handleDeleteFile,
  onSortChange,
  order,
  orderBy,
  isFetching,
  debouncedHandleFilterChange,
  handleCreateFolder,
  handleCreateFile,
  request,
  setPreviousLocation,
  applySearch,
}) => {
  const classes = useStyles();
  const [fileToRemove, setFileToRemove] = useState<FileToRemove>({});
  const [typeDelete, setTypeDelete] = useState(INITIALTYPEDELETE); // 2=folder,3=file
  const [navDirs, setNavDirs] = useState({
    actual: "all my files",
    lastDir: "",
  });

  headers[4].label = "";

  const clearData = () => {
    setFileToRemove({});
    setTypeDelete(INITIALTYPEDELETE);
  };
  const onNavegationMenuClick = (selected) => {
    if (selected.isDir) folderSelected(selected);
  };
  const deleteFolder = () => {
    handleDeleteFolder(fileToRemove.id);
  };
  const deleteFile = () => {
    handleDeleteFile(fileToRemove.id);
  };
  const handleConfirmChange = () => {
    if (typeDelete.type === 2) deleteFolder();
    else deleteFile();
    clearData();
  };
  const downloadFile = (file) => {
    return handleDownloadFile(file.id);
  };
  const setEmptyState = () => {
    if (deep.length === 1 && files.length === 0 && !isSearching)
      return (
        <Typography variant="body1" className={classes.noFiles}>
          No files found.
        </Typography>
      );
    if (files.length === 0)
      return (
        <Typography variant="body1" className={classes.noFiles}>
          No files found.
        </Typography>
      );
    return null;
  };
  const handleDownload = (file) => <RedirectKeepUTM to={downloadFile(file)} />;

  const handleDelete = (file) => {
    setFileToRemove(file);
    if (file.isDir)
      setTypeDelete({
        bodyMessage: `Are you sure to remove the folder ${file.name}?`,
        message: "Delete folder",
        isOpen: true,
        type: 2,
      });
    else
      setTypeDelete({
        bodyMessage: `Are you sure to remove this file ${file.name}?`,
        message: "Delete file",
        isOpen: true,
        type: 3,
      });
  };

  const handleLocationClick = (dir) => {
    if (dir.name !== navDirs.actual) setPreviousLocation(dir);
  };

  const getOptions = (file) => {
    const options = [
      <Tooltip title="Delete" key="delete">
        <TrashIcon
          className={classes.optionsIcon}
          onClick={() => handleDelete(file)}
        />
      </Tooltip>,
    ];

    if (!file.isDir) {
      options.unshift(
        <Tooltip title="Download" key="download">
          <DownloadIcon
            className={classes.downloadIcon}
            onClick={() => handleDownload(file)}
          />
        </Tooltip>
      );
    }

    if (file.isPrivate) {
      options.unshift(
        <Tooltip title="Private file">
          <LockIconShared className={classes.lockIcon} />
        </Tooltip>
      );
    }

    return options;
  };

  useEffect(() => {
    if (deep.length > 1) {
      setNavDirs({
        actual: deep[deep.length - 1].name,
        lastDir: deep[deep.length - 2].name,
      });
    } else {
      setNavDirs({
        actual: "All My Files",
        lastDir: "",
      });
    }
  }, [deep]);

  return (
    <>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="simple table">
          <SortableTableHead
            fields={headers}
            onRequestSort={onSortChange}
            order={order}
            orderBy={orderBy}
          />
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} className={classes.searchRow}>
                <div className={classes.navFlex}>
                  <div className={clsx(classes.flexCenter, classes.navDiv)}>
                    {deep.map((dir) => (
                      <>
                        <Typography
                          onClick={() => handleLocationClick(dir)}
                          className={
                            dir.name === navDirs.actual
                              ? classes.actualLocation
                              : classes.prevLocationStyle
                          }
                        >
                          {dir.name === "Home" ? "All My Files" : dir.name}
                        </Typography>
                        <span className={classes.separator}>
                          {dir.name !== navDirs.actual && ">"}
                        </span>
                      </>
                    ))}
                  </div>
                  <div>
                    <HeaderBrowser
                      isFetching={isFetching}
                      isSearching={isSearching}
                      debouncedHandleFilterChange={debouncedHandleFilterChange}
                      applySearch={applySearch}
                      handleCreateFolder={handleCreateFolder}
                      handleCreateFile={handleCreateFile}
                      request={request}
                    />
                  </div>
                </div>
              </TableCell>
            </TableRow>

            {files.map((file) => (
              <TableRow
                key={file.id}
                component="th"
                scope="row"
                className={clsx({ [classes.hover]: file.isDir })}
              >
                <TableCell
                  component="th"
                  scope="row"
                  onClick={() => onNavegationMenuClick(file)}
                >
                  {file.isDir ? (
                    <FolderIcon className={classes.nameIcon} />
                  ) : (
                    <FileIcon className={classes.nameIcon} />
                  )}
                  <span className={classes.tTruncate}>{file.name}</span>
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  onClick={() => onNavegationMenuClick(file)}
                >
                  {file.size}
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  onClick={() => onNavegationMenuClick(file)}
                >
                  <span className={classes.tTruncate}>
                    {formattedDate(file.dateUtc)}
                  </span>
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  onClick={() => onNavegationMenuClick(file)}
                >
                  <span className={classes.tTruncate}>{file.uploadedBy}</span>
                </TableCell>

                <TableCell align="center" component="th" scope="row">
                  <Grid item xs={12} className={classes.newFlex}>
                    {getOptions(file).map((option) => option)}
                  </Grid>
                </TableCell>
              </TableRow>
            ))}
            {setEmptyState()}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        bodyMessage={typeDelete.bodyMessage}
        message={typeDelete.message}
        isOpen={typeDelete.isOpen}
        handleComfirmDelete={handleConfirmChange}
        handleDialogClose={() => {
          setTypeDelete(INITIALTYPEDELETE);
        }}
        confirmDialogMessage={`Yes, delete ${
          typeDelete.type === 3 ? "file" : "folder"
        }`}
        customCss={classes.deleteDialog}
      />
    </>
  );
};
ContentFiles.propTypes = {
  files: PropTypes.instanceOf(Object).isRequired,
  folderSelected: PropTypes.func.isRequired,
  handleDeleteFolder: PropTypes.func.isRequired,
  handleDeleteFile: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  deep: PropTypes.instanceOf(Array).isRequired,
  isSearching: PropTypes.bool.isRequired,
  handleDownloadFile: PropTypes.func,
  isFetching: PropTypes.bool.isRequired,
  debouncedHandleFilterChange: PropTypes.func,
  handleCreateFolder: PropTypes.func,
  handleCreateFile: PropTypes.func,
  request: PropTypes.instanceOf(Object).isRequired,
  setPreviousLocation: PropTypes.func.isRequired,
  applySearch: PropTypes.func.isRequired,
};
ContentFiles.defaultProps = {
  handleDownloadFile: "",
  debouncedHandleFilterChange: "",
  handleCreateFolder: "",
  handleCreateFile: "",
};
